import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TableIconButton } from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  CollapseAllIcon,
  DateInput,
  ExpandAllIcon,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { InvoiceCategory } from '../../types/invoice';
import { CategorySummaryReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportCategorySummary } from './exportReports';

const categorySortGetters: Record<
  string,
  (
    row: CategorySummaryReport['data'][number],
  ) => string | number | boolean | null | undefined
> = {
  categoryName: (r) => r.categoryName,
  invoice: (r) => r.totals.invoice,
  cash: (r) => r.totals.cash,
  total: (r) => r.totals.total,
};

export function CategorySummaryPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [categories, setCategories] = useState<InvoiceCategory[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [report, setReport] = useState<CategorySummaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('categoryName');

  const displayRows = useMemo(
    () => (report ? sortItems(report.data, sort, categorySortGetters) : []),
    [report, sort],
  );

  useEffect(() => {
    void Promise.all([
      api.getCustomerLookup({ active: 'true' }),
      api.getInvoiceCategories(false),
    ]).then(([customerResult, categoryResult]) => {
      setCustomers(customerResult);
      setCategories(categoryResult);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCategorySummaryReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, customerId, categoryId]);

  function toggle(key: string) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    exportCategorySummary({
      report,
      rows: displayRows,
      meta: [
        { label: labels.dateFrom, value: formatDate(dateFrom || null) },
        { label: labels.dateTo, value: formatDate(dateTo || null) },
        {
          label: t().common.customer,
          value:
            customers.find((c) => String(c.id) === customerId)?.name ??
            labels.allCustomers,
        },
        {
          label: t().invoices.category,
          value:
            categories.find((c) => String(c.id) === categoryId)?.name ??
            labels.allCategories,
        },
      ],
      format,
    });
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => handleExport('excel')}
            onPdf={() => handleExport('pdf')}
          />
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().nav.customers}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.allCustomers}
            options={customers.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().nav.invoiceCategories}>
          <Select
            value={categoryId}
            size="sm"
            showClearButton={Boolean(categoryId)}
            placeholder={labels.allCategories}
            options={categories.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) => setCategoryId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading || !report ? (
          <LoadingSpinner message={t().common.loading} />
        ) : report.data.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="px-3 py-2 font-medium">{t().common.actions}</th>
                <SortableTh
                  sortKey="categoryName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().invoices.category}
                </SortableTh>
                <SortableTh
                  sortKey="invoice"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.invoice}
                </SortableTh>
                <SortableTh
                  sortKey="cash"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.cash}
                </SortableTh>
                <SortableTh
                  sortKey="total"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.grandTotal}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => {
                const key = String(row.categoryId ?? 'none');
                const open = Boolean(expanded[key]);
                return (
                  <Fragment key={key}>
                    <tr className="border-t border-line">
                      <td className="px-3 py-2">
                        <TableIconButton
                          label={
                            open ? labels.hideInvoices : labels.showInvoices
                          }
                          onClick={() => toggle(key)}
                        >
                          {open ? (
                            <CollapseAllIcon className="h-4 w-4" />
                          ) : (
                            <ExpandAllIcon className="h-4 w-4" />
                          )}
                        </TableIconButton>
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {row.categoryName}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.totals.invoice)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.totals.cash)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(row.totals.total)}
                      </td>
                    </tr>
                    {open
                      ? row.invoices.map((invoice) => (
                          <tr
                            key={`${key}-${invoice.id}`}
                            className="border-t border-line bg-surface-muted/40"
                          >
                            <td />
                            <td className="px-3 py-2 pl-8 text-ink-muted">
                              {formatDate(invoice.invoiceDate)} ·{' '}
                              <Link
                                to={`/customers/${invoice.customer.id}`}
                                className="text-brand hover:underline"
                              >
                                {invoice.customer.name}
                              </Link>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatMoney(invoice.totals.invoice)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatMoney(invoice.totals.cash)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <Link
                                to={`/invoices/${invoice.id}`}
                                className="text-brand hover:underline"
                              >
                                {invoice.invoiceNo} ·{' '}
                                {formatMoney(invoice.totals.total)}
                              </Link>
                            </td>
                          </tr>
                        ))
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot className="border-t border-line bg-surface-muted font-medium">
              <tr>
                <td />
                <td className="px-3 py-2">Σύνολα</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.invoice)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.cash)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
